"use client";
import React, { useCallback } from "react";
import Image from "next/image";
import Checkbox from "@/components/ui/checkbox/Checkbox";
import { FacetItem } from "@/common/lib/types";
import NoImageSrc from "@/assets/images/no_image.svg";
import Switch from "@/components/ui/switch/Switch";
import { translateFilterLabel } from "@/apiUtils/commonUtils";
import { facetTypeConstant } from "@/apiUtils/constants";

export interface TextListProps {
  facetName: string;
  facetType: string;
  facet: FacetItem[];
  selectedValues: string[];
  translations: { singleItem: string; multipleItem: string };
  onClick: (value: string) => void;
  lang: string;
  fullTranslations?: Record<string, string>;
  /** Akeneo declares this filter as icon tiles, so each value renders its Cloudinary icon. */
  showIcons?: boolean;
}

const TextList = ({
  facetName,
  facetType,
  facet,
  selectedValues,
  translations,
  lang,
  onClick,
  showIcons = false,
}: TextListProps) => {
  const handleChange = (value: string) => {
    onClick(value);
  };

  const getTranslatedLabel = useCallback(
    (label: string): string => {
      return facetType === facetTypeConstant.PACK_FILTER
        ? translateFilterLabel(lang, label, facetTypeConstant.PACK_FILTER, translations)
        : label;
    },
    [lang, translations],
  );

  return facet?.map((item, index) => {
    if (item?.value === "false") return <></>;
    return (
      <React.Fragment key={index}>
        <div
          className="flex cursor-pointer items-center justify-between p-4"
          onClick={() => handleChange(item?.value)}
        >
          {showIcons && (
            /*
             * `imageUrl` is a complete Cloudinary URL built from the attribute and value codes,
             * so it is used as-is. A value whose icon has not been uploaded falls back to the
             * placeholder rather than collapsing the row.
             */
            <Image
              src={item?.imageUrl || NoImageSrc}
              height={60}
              width={60}
              className="me-2"
              alt={item?.value}
            />
          )}
          <div className="flex flex-1 items-center justify-between">
            <p className="text-sm">
              {item?.value === "true" ? facetName : getTranslatedLabel(item?.label || "")}
            </p>
            <div className="flex items-center">
              <p className="me-2 text-xs text-tertiary-6">
                {item?.count > 1
                  ? translations?.multipleItem?.replace("{{count}}", `${item?.count}`)
                  : translations?.singleItem?.replace("{{count}}", `${item?.count}`)}
              </p>
              {item?.value === "true" ? (
                <Switch
                  bgClassName="has-[:checked]:bg-[#4285F4] bg-[#C4C4C4] pointer-events-none"
                  checked={selectedValues?.includes(item?.value)}
                  onChange={() => {}}
                />
              ) : (
                <Checkbox
                  checked={selectedValues?.includes(item?.value)}
                  className="h-4 w-4 border-tertiary-4 checked:bg-[#4285F4] hover:bg-white hover:checked:bg-[#4285F4] focus:checked:bg-[#4285F4]"
                  onChange={() => {}}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mx-4 h-[1px] bg-tertiary-7" />
      </React.Fragment>
    );
  });
};

export default TextList;
